import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserButton, PhaserButtonConfig } from "src/games/shared/Button";
import { GameState, DialogOption } from '../objects/GameState';
import { Slide } from '../objects/Slide';
import { Character } from "../objects/Character";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { Color } from "src/games/shared/ColorValue";
import TranslateApi from "src/utils/TranslateHelper";
import AudioTranslations from "src/constants/AudioTranslations";
import { numKeys } from "src/constants/KeyboardNumKeys";

export class BaseScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    slides: Slide[];
    map: Phaser.Tilemaps.Tilemap;
    optionArea1: TiledGameObject = null;
    optionArea2: TiledGameObject = null;
    optionArea3: TiledGameObject = null;
    dialogBoxArea: TiledGameObject = null;
    padding: number = 20;
    characterArea: TiledGameObject = null;
    chartOnScreen: boolean = false;
    dialogHeight: number;

    constructor() {
        super({
            key: "BaseScene" + Math.random()
        });
        bindAllOfThis(this, BaseScene.prototype);
    }

    preload(): void {
        // Slides
        this.slides = require("../objects/slides.json");

        // Skip the regular preload operations because they are already loaded
        if (GameState.current.isLoaded) {
            return;
        }

        // Catch load errors here
        this.load.on("loaderror", (file: Phaser.Loader.File) => {
            console.error(`File load error: ${file.key}`);
            // Audio file missed, load blank audio as it *should* always exist
            if (file.type === "audio") {
                this.load.audio(file.key, require("/src/assets/qci/sounds/blank_audio.mp3"));
            }
        });

        this.load.tilemapTiledJSON("map", require("/src/assets/qci/maps/colony.json"));
        this.load.atlas("assets", require("src/assets/qci/atlas/gameColony.png"), require("src/assets/qci/atlas/gameColony.json"));

        // Terrains (Backgrounds)
        this.load.image("terrain_r", require('/src/assets/qci/backgrounds/terrains/r.png'));
        this.load.image("terrain_i", require('/src/assets/qci/backgrounds/terrains/i.png'));
        this.load.image("terrain_s", require('/src/assets/qci/backgrounds/terrains/s.png'));
        this.load.image("terrain_c", require('/src/assets/qci/backgrounds/terrains/c.png'));
        // Charts
        this.load.image('chart1', require('/src/assets/qci/sprites/colonyGame/temp_chart1.png'));
        this.load.image('chart2', require('/src/assets/qci/sprites/colonyGame/temp_chart2.png'));
        this.load.image('chart3', require('/src/assets/qci/sprites/colonyGame/temp_chart3.png'));
        this.load.image('chart4', require('/src/assets/qci/sprites/colonyGame/temp_chart4.png'));
        this.load.image('chart5', require('/src/assets/qci/sprites/colonyGame/temp_chart5.png'));

        // Audio
        this.slides.forEach(slide => {
            this.load.audio(slide.dialogKey, AudioTranslations.FindByKeyAndLanguage(slide.dialogKey, TranslateApi.currentLanguage.code).url);
            slide.options.forEach(option => {
                this.load.audio(option.textKey, AudioTranslations.FindByKeyAndLanguage(option.textKey, TranslateApi.currentLanguage.code).url);
            });
        });

        GameState.current.isLoaded = true;
    }

    initialize(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: "map" });
    }

    create(): void {
        this.initialize();
        if (GameState.current.currentSlideID <= this.slides.length) {
            this.generateScene();
        }
        this.bindNumKeys();
    }

    generateScene(): void {
        this.addBackground();
        var currentSlideId = GameState.current.currentSlideID;

        this.map.getObjectLayer("Objects").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "character") {
                this.characterArea = ob;
            }
            else if (ob.name === "dialog") {
                this.dialogBoxArea = ob;
                this.dialogHeight = this.addDialog();
            }
            else if (ob.name === "option_1") {
                this.optionArea1 = ob;
            }
            else if (ob.name === "option_2") {
                this.optionArea2 = ob;
            }
            else if (ob.name === "option_3") {
                this.optionArea3 = ob;
            }
        });

        // Add chart if necessary
        if (currentSlideId === 27) {
            this.addChart(1);
        }
        else if (currentSlideId === 31) {
            this.addChart(2);
        }
        else if (currentSlideId === 46) {
            this.addChart(3);
        }
        else if (currentSlideId === 58) {
            this.addChart(4);
        }
        else if (currentSlideId === 73) {
            this.addChart(5);
        }

        // Load audio
        let currentSlide = this.slides.find(x => x.id === GameState.current.currentSlideID);
        let dialogSound = this.sound.add(currentSlide.dialogKey);
        dialogSound.play();
        if (currentSlide.options.length > 0) {
            let firstOptionKey = currentSlide.options[0].textKey;
            let firstOptionSound = this.sound.add(firstOptionKey);
            firstOptionSound.play("", { delay: dialogSound.totalDuration });

            if (currentSlide.options.length > 1) {
                let secondOptionKey = currentSlide.options[1].textKey;
                let secondOptionSound = this.sound.add(secondOptionKey);
                secondOptionSound.play("", { delay: dialogSound.totalDuration + firstOptionSound.totalDuration });
            }
        }

        this.addOptions();
        this.addCharacter();
    }

    /**
     * Adds the appropriate background to the scene according to the slideCharacter
     */
    addBackground(): void {
        let sky = this.add.image(0, 0, "pixel");
        sky.setOrigin(0);
        sky.setDisplaySize(this.gameWidth, this.gameHeight);
        sky.setTint(0x6eb8e5);

        let background = this.add.image(200, 0, GameState.current.slideCharacter.backgroundAsset);
        background.setOrigin(0);
        background.setPosition(0, this.gameHeight - background.height);

        var nameSet = new Set(GameState.current.slideCharacter.assetNames);
        // Librarius's background has Mr. Tiger pants somewhere (can take this out if the background itself includes him)
        if (nameSet.has("librarius")) {
            let tiger = this.add.image(0, 0, "assets", "tiger1");
            tiger.setOrigin(0);
            tiger.setDisplaySize(200, 100);
            tiger.setPosition(50, 650);
        }
    }

    addOptions(): void {
        if (GameState.current.slideOptions.length > 1) {
            // Use optionArea1 and optionArea2
            var option1 = this.createOptionButton(this.optionArea1, 0, false);
            var option2 = this.createOptionButton(this.optionArea2, 1, false);
            option2.setY(option1.y + option1.height + this.padding);
        }
        else {
            // Just optionArea3
            var singleOption = this.createOptionButton(this.optionArea3, 0, true);
            // Get best button size and center it under the dialog box area
            singleOption.setDisplaySize(singleOption.getIdealButtonWidth() + this.padding, singleOption.getIdealButtonHeight() + this.padding);
            singleOption.setX(this.dialogBoxArea.x + this.dialogBoxArea.width / 2 - singleOption.width / 2);
        }
    }

    createOptionButton(ob: TiledGameObject, index : number, isPrimary: boolean): PhaserButton {
        let option = GameState.current.slideOptions[index];
        let buttonConfig = new PhaserButtonConfig();
        buttonConfig.backgroundColor = isPrimary ? Color.KUDER_PRIMARY_BUTTON : Color.KUDER_SECONDARY_BUTTON;
        buttonConfig.smartHeight = true;
        let yPos = this.dialogHeight + 100;
        let optionButton = new PhaserButton(this, ob.x + 5, yPos + 5, ob.width, ob.height, (index + 1) + ' :' + TranslateApi.translate(option.textKey), buttonConfig);
        optionButton.setDepth(5);
        if (optionButton.getIdealButtonHeight() <= ob.height) {
            optionButton.setDisplaySize(optionButton.width, optionButton.getIdealButtonHeight());
        }

        optionButton.on('buttonClicked', () => {
            this.selectOption(option);
        });

        return optionButton;
    }

    addDialog(): number {
        var ob = this.dialogBoxArea;
        let dialogText = GameState.current.slideDialog;

        let dialog = this.add.text(ob.x, ob.y, dialogText);
        dialog.setOrigin(0);
        dialog.setPadding(this.padding, this.padding, this.padding, this.padding);
        dialog.setFontFamily('Roboto Mono, Arial');
        dialog.setFontSize(this.getDialogTextSize(dialogText));
        dialog.setWordWrapWidth(ob.width - (2 * this.padding), true);
        dialog.setDepth(10);
        dialog.setColor("#F8E71C");

        let height = this.getDialogHeight(ob.height, dialogText);

        // Background for the dialog that spans across the scene
        var outline = this.add.image(ob.x - 5, ob.y - 5, "pixel");
        outline.setOrigin(0);
        outline.setTint(GameState.current.slideCharacter.color);
        outline.setDisplaySize(ob.width + 10, height + 10);

        let dialogBackground = this.add.image(ob.x, ob.y, "pixel");
        dialogBackground.setOrigin(0);
        dialogBackground.setTint(0x000000);
        dialogBackground.setDepth(0);
        dialogBackground.setDisplaySize(ob.width, height);

        return height;
    }

    getDialogHeight(origHeight: number, dialogText: string): number {
        if (dialogText.length >= 840) {
            return origHeight;
        }

        let numLines = Math.ceil(dialogText.length / 60);
        return Math.min(40 + numLines * 25, origHeight);
    }

    getDialogTextSize(dialog: string): number {
        let l = dialog.length;
        if (l <= 840) { return 18; }
        l -= 840;
        let size = 17;
        while (l > 0 && size >= 1) {
            if (l <= 100) {
                return size;
            }

            l -= 70;
            size -= 1;
        }

        return size;
    }

    /**
     * Character is on the left of the screen and right on top of the dialog strip
     */
    addCharacter(): void {
        var ob = this.characterArea;
        var slide = this.slides[GameState.current.currentSlideID - 1];
        let character = this.add.image(ob.x, ob.y, "assets", slide.characterAssetKey);
        character.setOrigin(0);
        var char = this.findCharacter(slide);
        if (this.chartOnScreen) {
            character.setScale(char.scale - 0.1);
        }
        else {
            character.setScale(char.scale);
        }
        character.setY(this.gameHeight - character.displayHeight);
    }

    /**
     * Adds the specified chart to the scene to the top right corner
     */
    addChart(chartNumber: number): void {
        var chart = this.add.image(0, 0, "chart" + chartNumber);
        chart.setOrigin(0);
        chart.setScale(0.6);
        chart.setPosition(this.gameWidth - chart.getBounds().width - this.padding, this.padding);
        this.chartOnScreen = true;
    }

    /**
     * Will change the game state based on the option given
     */
    setNextGameState(option: DialogOption): void {
        var nextSlide = this.slides[option.nextSlideID - 1];
        GameState.current.slideDialog = TranslateApi.translate(nextSlide.dialogKey);
        GameState.current.slideOptions = nextSlide.options;
        GameState.current.slideCharacter = this.findCharacter(nextSlide);
        GameState.current.currentSlideID = option.nextSlideID;
    }

    getBestButtonSize(button1: PhaserButton, button2: PhaserButton): Phaser.Math.Vector2 {
        var maxWidth = Math.max(button1.getIdealButtonWidth(), button2.getIdealButtonWidth()) + this.padding;
        var maxHeight = Math.max(button1.getIdealButtonHeight(), button2.getIdealButtonHeight()) + this.padding;
        var actualMaxWidth = Math.min(maxWidth, this.optionArea1.width - this.padding);
        var actualMaxHeight = Math.min(maxHeight, this.optionArea1.height - this.padding);
        return new Phaser.Math.Vector2(actualMaxWidth, actualMaxHeight);
    }

    findCharacter(slide: Slide): Character {
        for (var c of Character.CHARACTERS) {
            var nameSet = new Set(c.assetNames);
            if (nameSet.has(slide.characterAssetKey)) {
                return c;
            }
        }
        return null;
    }

    selectOption(option: DialogOption): void {
        this.sound.stopAll();
        if (option.nextSlideID != null) {
            this.setNextGameState(option);
            this.scene.remove(this);
            this.scene.add("BaseScene", BaseScene, true);
        }
        else {
            // Game Over
            (this.sys.game as any).onComplete();
        }
    }

    onKeydown(event: Function, ...keys: string[]): void {
        keys.forEach((key: string) => {
            this.input.keyboard.on('keydown_' + key, event);
        });
    }

    bindNumKeys(): void {
        numKeys.forEach((key) => {
            this.onKeydown(() => {
                let slideOption: DialogOption = GameState.current.slideOptions[key.index];
                if (slideOption) {
                    this.selectOption(slideOption);
                }
            }, key.keyCode, key.altKeyCode);
        });
    }
}



// WEBPACK FOOTER //
// ./src/games/ColonyGame/scenes/BaseScene.ts